import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, usePageMeta } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { CookieBanner } from "@/components/CookieBanner";
import { Reveal } from "@/components/Reveal";

const SORTS = [
  { value: "popular", label: "Más populares" },
  { value: "price_asc", label: "Precio: de menor a mayor" },
  { value: "price_desc", label: "Precio: de mayor a menor" },
  { value: "new", label: "Novedades" },
];

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("popular");
  const [notFound, setNotFound] = useState(false);

  usePageMeta(
    category ? `${category.name} · solsdiarias360` : "Categoría · solsdiarias360",
    category?.description
  );

  useEffect(() => {
    setNotFound(false);
    api.get("/categories").then((r) => {
      const found = r.data.find((c) => c.slug === slug);
      setCategory(found || null);
      if (!found) setNotFound(true);
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    api.get("/products", { params: { category: slug, sort, limit: 100 } })
      .then((r) => setProducts(r.data.products))
      .catch(() => {});
  }, [slug, sort]);

  return (
    <div className="App">
      <Header />
      <main className="bg-slate-50 min-h-screen">
        <div className="bg-slate-950 text-white hero-grain relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <p className="text-amber-400 font-mono-s360 text-xs uppercase tracking-[0.22em] mb-2">Categoría</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="category-title">
              {category ? category.name : "Categoría"}
            </h1>
            {category && (
              <p className="text-slate-400 text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">{category.description}</p>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <p className="text-sm text-slate-600" data-testid="category-count">
              {products.length} {products.length === 1 ? "producto" : "productos"}
            </p>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Ordenar por
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="category-sort-select"
                className="bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          </div>

          {notFound ? (
            <p className="text-slate-600">Esta categoría no existe.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}

          <Reveal className="mt-12">
            <p className="text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="category-disclosure">
              Como asociado de Amazon, solsdiarias360 obtiene ingresos por las compras adscritas que
              cumplen los requisitos aplicables. Los precios y la disponibilidad pueden cambiar en
              Amazon.es.
            </p>
          </Reveal>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
